import { createContext, useContext, useEffect, useState } from "react";
import api, { clearResolvedWorkspace, isTenantSurface, setResolvedWorkspace } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("aetria_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("aetria_token");
        setUser(false);
      });
  }, []);

  const login = async (email, password, remember = false, workspace = null) => {
    if (isTenantSurface()) {
      // White-label tenant surface (?tenant= / subdomain) — unchanged path.
      const { data } = await api.post("/auth/login", { email, password, remember });
      localStorage.setItem("aetria_token", data.access_token);
      setUser(data.user);
      return data;
    }
    // Base domain — general Aetria OS login with cross-workspace resolution.
    const body = { email, password, remember };
    if (workspace) body.workspace = workspace;
    const { data } = await api.post("/auth/general/login", body);
    if (data.status === "choose_workspace") return data; // no tokens issued yet
    if (data.account_type === "platform") {
      localStorage.setItem("aetria_platform_token", data.access_token);
      return data; // caller redirects to /platform
    }
    setResolvedWorkspace(data.tenant.subdomain);
    localStorage.setItem("aetria_token", data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("aetria_token");
    clearResolvedWorkspace(); // base surface returns to the general login
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
